/**
 * R3.2 — GelAgentsRedirect
 *
 * Thin redirect shim that retires the legacy /gel-agents page in favour of
 * the canonical /agents?type=ExteLet inventory. All of the rich functionality
 * the old slideout used to host (Verify probe, Suspend/Revoke, skill mappings,
 * governance wizard, integration tests, runtime bindings) now lives on the
 * Registration tab of AgentDetailPageV2 — see R3.3.a-d, R2.3, R4.
 *
 * Deep-link preservation:
 *   - /gel-agents                            → /agents?type=ExteLet
 *   - /gel-agents?selected=<registrationId>  → /agents/<agentId>?tab=registration
 *     (the registrationId is resolved to an agentId via gelGetRegistration so
 *     Discovery Promote and any bookmarks still land on the correct ExteLet)
 *   - /gel-agents?agentId=<agentId>          → /agents/<agentId>?tab=registration
 *
 * The resolution is fast (single API call) so we render a tiny inline
 * "Loading…" affordance during the lookup — the operator never sees a blank
 * screen.
 */
import React, { useEffect, useState } from 'react';
import { Navigate, useSearchParams } from 'react-router-dom';
import * as api from '../services/api';

export default function GelAgentsRedirect() {
  const [searchParams] = useSearchParams();
  const selectedRegId = searchParams.get('selected');
  const agentIdParam = searchParams.get('agentId');
  /* `null` while we're still resolving; a string once we know where to send
   * the operator; the empty string when there's no deep-link to preserve. */
  const [resolvedAgentId, setResolvedAgentId] = useState(
    agentIdParam || (selectedRegId ? null : '')
  );

  useEffect(() => {
    if (resolvedAgentId !== null) return; // already resolved
    if (!selectedRegId) {
      setResolvedAgentId('');
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await api.gelGetRegistration?.(selectedRegId);
        const row = res?.data || res;
        const agentId = row?.agentId || row?.agent_id || '';
        if (!cancelled) setResolvedAgentId(typeof agentId === 'string' ? agentId : '');
      } catch {
        if (!cancelled) setResolvedAgentId('');
      }
    })();
    return () => { cancelled = true; };
  }, [selectedRegId, resolvedAgentId]);

  if (resolvedAgentId === null) {
    return (
      <div className="min-h-full p-7 text-[12.5px] text-v2-ink-muted">
        Redirecting to the unified Agentlets inventory…
      </div>
    );
  }

  /* R3.2 — Resolution complete. Either we have an agentId (deep-link to the
   * detail page Registration tab) or we fall back to the inventory pre-
   * filtered to ExteLets. `replace` so the legacy URL doesn't pile up in
   * browser history. */
  if (resolvedAgentId) {
    return <Navigate to={`/agents/${resolvedAgentId}?tab=registration`} replace />;
  }
  return <Navigate to="/agents?type=ExteLet" replace />;
}
